import React, { useEffect, useRef, useState } from "react";
import "./style.scss";
import html2pdf from "html2pdf.js";
import Logo from "../../assets/logo.png";
import axios from "axios";
import { apiUrl } from "../../constants";

const Invoice = ({ invoiceData, onInvoiceDownloaded }) => {
  const invoiceRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("");
  const calculateTaxes = (amount, rate) => (amount * rate) / 100;
  const totalAmount = invoiceData.total_amount;
  const totalCGST = calculateTaxes(invoiceData.total_amount, 9);
  const totalSGST = calculateTaxes(invoiceData.total_amount, 9);
  const totalTax = totalCGST + totalSGST;

  let grandTotal = totalAmount + totalTax;
  if (
    invoiceData.custom_discount ||
    invoiceData.premiumDiscount ||
    invoiceData.couponDiscount
  ) {
    const customDiscount = invoiceData.custom_discount || 0;
    const premiumDiscount = invoiceData.premiumDiscount || 0;
    const couponDiscount = invoiceData.couponDiscount?.discount || 0;
    grandTotal -= customDiscount + premiumDiscount + couponDiscount;
  }

  useEffect(() => {
    if (
      invoiceData.custom_discount ||
      invoiceData.premiumDiscount ||
      invoiceData.couponDiscount
    ) {
      const customDiscount = invoiceData.custom_discount || 0;
      const premiumDiscount = invoiceData.premiumDiscount || 0;
      const couponDiscount = invoiceData.couponDiscount?.discount || 0;
      const totalDiscount = customDiscount + premiumDiscount + couponDiscount;

      setDiscount(totalDiscount);

      if (customDiscount !== 0) {
        setDiscountType("CUSTOM");
      } else if (totalDiscount != 0) {
        setDiscountType("COUPON");
      } else {
        setDiscountType("NA");
      }
    } else {
      setDiscount(0);
      setDiscountType("");
    }
  }, [invoiceData]);

  const getCurrentSno = async () => {
    try {
      const invoiceSnoData = await axios.get(`${apiUrl}/bill/billsno`);
      let currentInvoiceNumber = invoiceSnoData?.data?.message || 0;
      setInvoiceNumber(currentInvoiceNumber);
    } catch (error) {
      console.error("Error fetching invoice number:", error);
      setInvoiceNumber(1);

      setErrorMessage("Error fetching invoice number: " + error.message);
    }
  };

  const currentDate = new Date().toLocaleDateString("en-US");
  const gstNumber = "23AAJFF2527Q1Z5";
  const hsnCode = "999699";

  const downloadPDF = async (pdfBlob) => {
    try {
      setIsProcessing(false);

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice_${invoiceNumber}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("PDF downloaded successfully");
      if (onInvoiceDownloaded && typeof onInvoiceDownloaded === "function") {
        onInvoiceDownloaded();
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setErrorMessage("Error downloading invoice: " + error.message);
    }
  };

  const postBillPayment = async () => {
    try {
      const salesId = await axios.get(`${apiUrl}/insights/salesId`);
      let Id = salesId.data.nextId;
      const [month, day, year] = currentDate.split("/");
      const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;

      const responseSales = await axios.post(`${apiUrl}/insights/salesadd`, {
        _id: Id,
        date: formattedDate,
        amount: grandTotal,
        section: "Ticket Counter",
        split: "Activities",
      });
      const response = await axios.post(`${apiUrl}/bill/billpayment`, {
        sno: invoiceNumber,
        Date: currentDate,
        CashAmount: invoiceData.cash_amount,
        OnlineAmount: invoiceData.online_amount,
        paymentType: invoiceData.payment_mode,
        amount: totalAmount,
        discount,
        discountType,
        cgst: totalCGST,
        sgst: totalSGST,
        total: grandTotal,
        CustGstNumber: invoiceData.customerGstNumber,
        phone_no: invoiceData.phoneNumber,
        name: invoiceData.name,
      });
      if (response.data.success) {
        console.log("Data is added");
      } else {
        console.error("Failed to add data");
      }
    } catch (error) {
      console.error("Error posting bill payment:", error);
    }
  };

  const generateAndDownloadPDF = async () => {
    try {
      setIsProcessing(true);
      setErrorMessage("");

      setShowInvoice(true);

      await new Promise((resolve) => setTimeout(resolve, 500));

      const invoiceContent = invoiceRef.current;

      if (!invoiceContent) {
        console.error("Invoice content not available");
        setErrorMessage("Invoice content not available");
        return;
      }

      const options = {
        margin: 10,
        filename: `Invoice_${invoiceNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      const pdfBlob = await html2pdf()
        .set(options)
        .from(invoiceContent)
        .output("blob");

      console.log("PDF generated successfully, size:", pdfBlob.size, "bytes");

      setShowInvoice(false);

      await downloadPDF(pdfBlob);
    } catch (error) {
      console.error("Error in PDF generation or download:", error);
      setErrorMessage("Error processing invoice: " + error.message);
      setShowInvoice(false);
    }
  };

  useEffect(() => {
    if (invoiceData) {
      getCurrentSno();
    }
  }, [invoiceData]);

  useEffect(() => {
    if (invoiceData && invoiceNumber !== null) {
      const timer = setTimeout(() => {
        generateAndDownloadPDF();
        postBillPayment();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [invoiceData, invoiceNumber]);

  // Function to get package details with costs
  const getPackageDetails = () => {
    const regularPackages = invoiceData.details.filter(
      (item) => item.packageType === "regular"
    );
    const unlimitedPackages = invoiceData.details.filter(
      (item) => item.packageType === "unlimited"
    );

    const packages = [];

    let unlimitedData = unlimitedPackages.map((current, ind) => {
      return current.unlimitedPackageData;
    });

    if (unlimitedPackages.length > 0) {
      const unlimitedTotal = unlimitedData.reduce(
        (sum, pkg) => sum + (pkg.price || 0),
        0
      );
      if (regularPackages.length > 0) {
        const regularTotal = invoiceData.total_amount - unlimitedTotal;
        packages.push({
          type: "Coin Based Package",
          cost: regularTotal,
          count: regularPackages.length,
        });
      }

      packages.push({
        type: "Unlimited Package",
        cost: unlimitedTotal,
        count: unlimitedPackages.length,
      });
    } else {
      if (regularPackages.length > 0) {
        const regularTotal = invoiceData.total_amount;
        packages.push({
          type: "Coin Based Package",
          cost: regularTotal,
          count: regularPackages.length,
        });
      }
    }

    return packages;
  };

  const packageDetails = getPackageDetails();

  return (
    <div>
      {isProcessing && <div className="upload-status">Generating PDF...</div>}
      {errorMessage && (
        <div className="upload-status error">{errorMessage}</div>
      )}

      {/* Only show invoice when invoice number is available */}
      {showInvoice && invoiceNumber !== null && (
        <div ref={invoiceRef}>
          <div id="invoice-main">
            <div id="upperDiv">
              <h3>S.NO.: {invoiceNumber}</h3>
              <h3>TAX INVOICE</h3>
              <img src={Logo} alt="Company Logo" />
            </div>

            <div id="lowerDiv">
              <h3>Date: {currentDate}</h3>
              <h3>GST No.: {gstNumber}</h3>
              <h3>HSN CODE.: {hsnCode}</h3>
            </div>

            {/* Customer Information */}
            <div
              id="customerInfo"
              style={{
                margin: "10px 0",
                padding: "10px",
                border: "1px solid #ccc",
              }}
            >
              <p>
                <strong>Name:</strong> {invoiceData.name}
              </p>
              <p>
                <strong>Phone:</strong> {invoiceData.phone_no}
              </p>
              <p>
                <strong>DOB:</strong> {invoiceData.dob}
              </p>
              <p>
                <strong>Total Persons:</strong> {invoiceData.count}
              </p>
              {invoiceData.count_unlimited_ticket > 0 && (
                <p>
                  <strong>Unlimited Package Persons:</strong>{" "}
                  {invoiceData.count_unlimited_ticket}
                </p>
              )}
              {invoiceData.coupon && (
                <p>
                  <strong>Coupon Applied:</strong> {invoiceData.coupon}
                </p>
              )}
              {invoiceData.custom_discount > 0 && (
                <p>
                  <strong>Custom Discount:</strong> ₹
                  {invoiceData.custom_discount}
                </p>
              )}
            </div>

            <div id="mode">
              <h5>PAYMENT MODE: {invoiceData.payment_mode.toUpperCase()}</h5>
            </div>

            <div id="table">
              <table>
                <thead>
                  <tr id="head">
                    <th
                      className="invoicehead"
                      style={{ borderRight: "3px solid black" }}
                    >
                      S.No
                    </th>
                    <th
                      className="invoicehead"
                      style={{ borderRight: "3px solid black" }}
                    >
                      Package
                    </th>
                    <th
                      className="invoicehead"
                      style={{ borderRight: "3px solid black" }}
                    >
                      Amount
                    </th>
                    <th className="invoicehead">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {packageDetails.map((pkg, index) => (
                    <tr key={index} id="datail">
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          marginBottom: "10px",
                          paddingBottom: "10px",
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          borderRight: "3px solid black",
                          marginBottom: "10px",
                          paddingBottom: "10px",
                        }}
                      >
                        {pkg.type}
                        {pkg.count > 1 && <span> (x{pkg.count})</span>}
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderBottom: "3px solid black",
                          marginBottom: "10px",
                          paddingBottom: "10px",
                        }}
                      >
                        ₹{pkg.cost.toFixed(2)}
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          borderRight: "3px solid black",
                          marginBottom: "10px",
                          paddingBottom: "10px",
                        }}
                      >
                        ₹{pkg.cost.toFixed(2)}
                      </td>
                    </tr>
                  ))}

                  {/* Tax calculation row */}
                  <tr id="datail">
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        paddingBottom: "10px",
                      }}
                    >
                      {packageDetails.length + 1}
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        borderRight: "3px solid black",
                        paddingBottom: "10px",
                      }}
                    >
                      Tax Calculation
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderBottom: "3px solid black",
                        paddingBottom: "10px",
                      }}
                    >
                      <ul>
                        <li
                          className="listInvoiceAmount"
                          style={{ borderBottom: "2px solid black" }}
                        >
                          Subtotal: ₹{totalAmount.toFixed(2)}
                        </li>
                        <li
                          className="listInvoiceAmount"
                          style={{ borderBottom: "2px solid black" }}
                        >
                          CGST 9%: ₹{totalCGST.toFixed(2)}
                        </li>
                        <li
                          className="listInvoiceAmount"
                          style={{ borderBottom: "2px solid black" }}
                        >
                          SGST 9%: ₹{totalSGST.toFixed(2)}
                        </li>
                        <li style={{ padding: "10px" }}>
                          Total Tax: ₹{totalTax.toFixed(2)}
                        </li>
                      </ul>
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        borderRight: "3px solid black",
                        paddingBottom: "10px",
                      }}
                    >
                      ₹{totalTax.toFixed(2)}
                    </td>
                  </tr>

                  {/* Discount row - only show if discount exists */}
                  {discount > 0 && (
                    <tr id="datail">
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          paddingBottom: "10px",
                        }}
                      >
                        {packageDetails.length + 2}
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          borderRight: "3px solid black",
                          paddingBottom: "10px",
                        }}
                      >
                        Discount ({discountType})
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderBottom: "3px solid black",
                          paddingBottom: "10px",
                          color: "red",
                        }}
                      >
                        -₹{discount.toFixed(2)}
                      </td>
                      <td
                        className="invoicedatatable"
                        style={{
                          borderLeft: "3px solid black",
                          borderBottom: "3px solid black",
                          borderRight: "3px solid black",
                          paddingBottom: "10px",
                          color: "red",
                        }}
                      >
                        -₹{discount.toFixed(2)}
                      </td>
                    </tr>
                  )}

                  {/* Total Payment row */}
                  <tr id="datail">
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        paddingBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {discount > 0
                        ? packageDetails.length + 3
                        : packageDetails.length + 2}
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        borderRight: "3px solid black",
                        paddingBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      TOTAL PAYMENT
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderBottom: "3px solid black",
                        paddingBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      ₹{grandTotal.toFixed(2)}
                    </td>
                    <td
                      className="invoicedatatable"
                      style={{
                        borderLeft: "3px solid black",
                        borderBottom: "3px solid black",
                        borderRight: "3px solid black",
                        paddingBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      ₹{grandTotal.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                id="accountDetails"
                style={{
                  border: "3px solid black",
                  marginTop: "1rem",
                  height: "8rem",
                  padding: "20px",
                }}
              >
                <h3 style={{ marginBottom: "8px" }}>Bank Name: YES BANK</h3>
                <h3 style={{ marginBottom: "8px" }}>
                  A/C No.: 044463700000949
                </h3>
                <h3 style={{ marginBottom: "8px" }}>IFSC CODE: YESB0000444</h3>
              </div>

              <h4 style={{ marginTop: "14px" }}>
                This is a system-generated invoice and does not require
                signature or Stamp.
              </h4>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
